import React, { Suspense } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getAppTitle } from "@/utils/domainUtils";

// Context Providers
import { LanguageProvider } from "@/contexts/LanguageContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { HolidayThemeProvider } from "@/contexts/HolidayThemeContext";
import { ColorThemeProvider } from "@/contexts/ColorThemeContext";
import { SystemSettingsProvider } from "@/contexts/SystemSettingsContext";
import { GlobalSettingsListener } from "@/components/GlobalSettingsListener";

// Import critical components synchronously
import { LanguageErrorBoundary } from "@/components/LanguageErrorBoundary";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import DomainRouter from "@/components/DomainRouter";

// Retry wrapper for dynamic imports — handles stale chunk failures after deploys
function lazyRetry(importFn: () => Promise<any>) {
  return React.lazy(() =>
    importFn().catch((error: any) => {
      console.warn('🔄 Dynamic import failed, retrying once...', error?.message);
      // Retry once — if SW served stale reference, second attempt may use network
      return importFn();
    })
  );
}

// Lazy load non-critical UI components (with retry for stale chunk resilience)
const Toaster = lazyRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazyRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const AuthCodeExchange = lazyRetry(() => import("@/components/AuthCodeExchange"));
const NotificationToastProvider = lazyRetry(() => import("@/components/NotificationToastProvider"));
const GlobalRealtimeNotifications = lazyRetry(() => import("@/components/GlobalRealtimeNotifications").then(m => ({ default: m.GlobalRealtimeNotifications })));
const GlobalPaymentNotifications = lazyRetry(() => import("@/components/GlobalPaymentNotifications"));
const WhatsAppFloatingButton = lazyRetry(() => import("@/components/WhatsAppFloatingButton").then(m => ({ default: m.WhatsAppFloatingButton })));
const ThemeProfileLoader = lazyRetry(() => import("@/components/ThemeProfileLoader"));
const PWAInstallModal = lazyRetry(() => import("@/components/PWAInstallModal").then(m => ({ default: m.PWAInstallModal })));

// Optimized React Query configuration with aggressive caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes (increased from 5)
      gcTime: 1000 * 60 * 60, // 1 hour (increased from 30 minutes)
      refetchOnWindowFocus: false, // Disable auto-refetch on window focus
      refetchOnMount: false, // Disable auto-refetch on component mount
      retry: 1, // Reduce retry attempts from 3 to 1
    },
  },
});

function App() {
  // Set dynamic page title based on domain
  React.useEffect(() => {
    document.title = getAppTitle();
  }, []);

  console.log('🚀 App: Starting App component');
  
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <TooltipProvider>
            <LanguageProvider>
              <NotificationProvider>
                <HolidayThemeProvider>
                  <ColorThemeProvider>
                    <SystemSettingsProvider>
                      <GlobalSettingsListener>
                        <Suspense fallback={null}>
                          <NotificationToastProvider />
                          <GlobalRealtimeNotifications />
                          <GlobalPaymentNotifications />
                          <ThemeProfileLoader />
                          <Toaster />
                          <Sonner />
                          <AuthCodeExchange />
                        </Suspense>
                        <LanguageErrorBoundary>
                          <DomainRouter />
                        </LanguageErrorBoundary>
                        <Suspense fallback={null}>
                          <WhatsAppFloatingButton />
                          <PWAInstallModal />
                        </Suspense>
                      </GlobalSettingsListener>
                    </SystemSettingsProvider>
                  </ColorThemeProvider>
                </HolidayThemeProvider>
              </NotificationProvider>
            </LanguageProvider>
          </TooltipProvider>
        </Router>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

export default App;